/**
 * Relatórios do simulador (área «Relatórios», só gestores): agregações sobre
 * as propostas. Puro — o servidor (`GET /api/reporting/proposals`) devolve
 * uma linha por proposta com as datas dos eventos e o ecrã agrega conforme os
 * filtros.
 *
 * Cada evento conta no mês em que aconteceu: criada (`created_at`), enviada
 * (1.º envio do PDF por email ou passagem a «enviada»), ganha, perdida.
 * «Em rascunho» = criadas no período que continuam em rascunho.
 */

export type ReportKind = "solar" | "ac" | "bc_aqs" | "bc_aq"
export type ReportStatus = "draft" | "under_review" | "sent" | "ganho" | "perdido"

export interface ReportRow {
  id: string
  number: string
  kind: ReportKind
  status: ReportStatus
  client_name: string
  seller_id: string | null
  seller_name: string | null
  /** Chave de LEAD_ORIGINS, ou null (propostas antigas). */
  origin: string | null
  /** Quem gerou a proposta: `user` (comercial) ou `ai` (formulário do site). Ausente num backend antigo = `user`. */
  generated_by?: "user" | "ai"
  created_at: string
  sent_at: string | null
  won_at: string | null
  lost_at: string | null
  /** Total s/ IVA. */
  value: number
  /** Custo (para a margem). */
  cost: number
}

export interface ReportFilters {
  /** Inclusive, `YYYY-MM-DD`. */
  from: string
  /** Inclusive, `YYYY-MM-DD`. */
  to: string
  seller: string | null
  kind: ReportKind | null
  origin: string | null
  /** Origem da proposta: só as geradas por IA ou só as dos comerciais (null = todas). */
  generatedBy?: "user" | "ai" | null
}

export const NO_ORIGIN = "__none__"
export const NO_SELLER = "__none__"

export const KIND_LABEL: Record<ReportKind, string> = {
  solar: "Solar",
  ac: "Ar condicionado",
  bc_aqs: "BC AQS",
  bc_aq: "BC aquecimento",
}

export const STATUS_LABEL: Record<ReportStatus, string> = {
  draft: "Rascunho",
  under_review: "Em revisão",
  sent: "Enviada",
  ganho: "Ganha",
  perdido: "Perdida",
}
export const STATUS_ORDER: ReportStatus[] = ["draft", "under_review", "sent", "ganho", "perdido"]

const MONTHS_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"]

/** `YYYY-MM` da data, no fuso do browser. */
export function monthKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
}

/** «out 26». */
export function monthLabel(key: string): string {
  const [y, m] = key.split("-")
  return `${MONTHS_PT[Number(m) - 1]} ${y!.slice(2)}`
}

/** Meses de `from` a `to` (inclusive). */
export function monthsBetween(from: string, to: string): string[] {
  const out: string[] = []
  const d = new Date(`${from.slice(0, 7)}-01T12:00:00`)
  const end = to.slice(0, 7)
  while (out.length < 120) {
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
    out.push(k)
    if (k >= end) break
    d.setMonth(d.getMonth() + 1)
  }
  return out
}

function dayKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

const inRange = (iso: string | null, f: ReportFilters) => {
  if (!iso) return false
  const k = dayKey(iso)
  return k >= f.from && k <= f.to
}

export interface Counts {
  created: number
  draft: number
  sent: number
  won: number
  lost: number
  wonValue: number
}
const zero = (): Counts => ({ created: 0, draft: 0, sent: 0, won: 0, lost: 0, wonValue: 0 })

export interface SellerReport extends Counts {
  id: string
  name: string
  conversion: number | null
  months: Record<string, Counts>
}

export interface Report {
  months: string[]
  totals: Counts & {
    conversion: number | null
    avgTicket: number | null
    /** Propostas enviadas/em revisão, por fechar (criadas no período). */
    openCount: number
    openValue: number
    /** Dias médios da criação ao ganho (ganhas no período). */
    avgDaysToWin: number | null
    /** Margem média sobre o custo, das ganhas no período. */
    avgMarginPct: number | null
  }
  byMonth: Array<Counts & { month: string; label: string }>
  byStatus: Array<{ status: ReportStatus; label: string; count: number }>
  byKind: Array<Counts & { kind: ReportKind; label: string }>
  byOrigin: Array<Counts & { origin: string; conversion: number | null }>
  bySeller: SellerReport[]
}

function add(c: Counts, row: ReportRow, f: ReportFilters, month: string | null) {
  const m = (iso: string | null) => (month == null ? inRange(iso, f) : inRange(iso, f) && monthKey(iso!) === month)
  if (m(row.created_at)) {
    c.created++
    if (row.status === "draft") c.draft++
  }
  if (m(row.sent_at)) c.sent++
  if (m(row.won_at)) {
    c.won++
    c.wonValue += row.value
  }
  if (m(row.lost_at)) c.lost++
}

const ratio = (a: number, b: number) => (b > 0 ? a / b : null)

/** Linhas que passam os filtros de vendedor, módulo, origem e quem gerou (as datas contam por evento). */
export function filterRows(rows: ReportRow[], f: ReportFilters): ReportRow[] {
  return rows.filter(
    (r) =>
      (!f.seller || (r.seller_id ?? NO_SELLER) === f.seller) &&
      (!f.kind || r.kind === f.kind) &&
      (!f.origin || (r.origin ?? NO_ORIGIN) === f.origin) &&
      (!f.generatedBy || (r.generated_by ?? "user") === f.generatedBy),
  )
}

export function buildReport(all: ReportRow[], f: ReportFilters): Report {
  const rows = filterRows(all, f)
  const months = monthsBetween(f.from, f.to)

  const totals = zero()
  for (const r of rows) add(totals, r, f, null)

  const byMonth = months.map((month) => {
    const c = zero()
    for (const r of rows) add(c, r, f, month)
    return { month, label: monthLabel(month), ...c }
  })

  const createdIn = rows.filter((r) => inRange(r.created_at, f))
  const byStatus = STATUS_ORDER.map((status) => ({
    status,
    label: STATUS_LABEL[status],
    count: createdIn.filter((r) => r.status === status).length,
  }))

  const byKind = (Object.keys(KIND_LABEL) as ReportKind[]).map((kind) => {
    const c = zero()
    for (const r of rows) if (r.kind === kind) add(c, r, f, null)
    return { kind, label: KIND_LABEL[kind], ...c }
  })

  const originKeys = Array.from(new Set(rows.map((r) => r.origin ?? NO_ORIGIN)))
  const byOrigin = originKeys
    .map((origin) => {
      const c = zero()
      for (const r of rows) if ((r.origin ?? NO_ORIGIN) === origin) add(c, r, f, null)
      return { origin, ...c, conversion: ratio(c.won, c.sent) }
    })
    .filter((o) => o.created + o.sent + o.won + o.lost > 0)
    .sort((a, b) => b.created - a.created)

  const sellerIds = Array.from(new Set(rows.map((r) => r.seller_id ?? NO_SELLER)))
  const bySeller = sellerIds
    .map((id) => {
      const mine = rows.filter((r) => (r.seller_id ?? NO_SELLER) === id)
      const c = zero()
      for (const r of mine) add(c, r, f, null)
      const monthsMap: Record<string, Counts> = {}
      for (const m of months) {
        const mc = zero()
        for (const r of mine) add(mc, r, f, m)
        monthsMap[m] = mc
      }
      return { id, name: mine[0]?.seller_name ?? "Sem vendedor", ...c, conversion: ratio(c.won, c.sent), months: monthsMap }
    })
    .filter((s) => s.created + s.sent + s.won + s.lost > 0)
    .sort((a, b) => b.wonValue - a.wonValue || b.won - a.won || b.created - a.created)

  const wonIn = rows.filter((r) => inRange(r.won_at, f))
  const days = wonIn.map((r) => (new Date(r.won_at!).getTime() - new Date(r.created_at).getTime()) / 86_400_000).filter((d) => d >= 0)
  const margins = wonIn.filter((r) => r.cost > 0 && r.value > 0).map((r) => (r.value - r.cost) / r.cost)
  const open = createdIn.filter((r) => r.status === "sent" || r.status === "under_review")

  return {
    months,
    totals: {
      ...totals,
      conversion: ratio(totals.won, totals.sent),
      avgTicket: totals.won > 0 ? totals.wonValue / totals.won : null,
      openCount: open.length,
      openValue: open.reduce((s, r) => s + r.value, 0),
      avgDaysToWin: days.length ? days.reduce((s, d) => s + d, 0) / days.length : null,
      avgMarginPct: margins.length ? margins.reduce((s, d) => s + d, 0) / margins.length : null,
    },
    byMonth,
    byStatus,
    byKind,
    byOrigin,
    bySeller,
  }
}

/** Presets do período. */
export type PeriodPreset = "this_month" | "last_month" | "last_3" | "last_6" | "last_12" | "this_year" | "custom"
export const PERIOD_LABEL: Record<PeriodPreset, string> = {
  this_month: "Este mês",
  last_month: "Mês passado",
  last_3: "Últimos 3 meses",
  last_6: "Últimos 6 meses",
  last_12: "Últimos 12 meses",
  this_year: "Este ano",
  custom: "Personalizado",
}

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

export function presetRange(p: Exclude<PeriodPreset, "custom">, today = new Date()): { from: string; to: string } {
  const y = today.getFullYear()
  const m = today.getMonth()
  const first = (yy: number, mm: number) => new Date(yy, mm, 1, 12)
  const last = (yy: number, mm: number) => new Date(yy, mm + 1, 0, 12)
  switch (p) {
    case "this_month": return { from: ymd(first(y, m)), to: ymd(today) }
    case "last_month": return { from: ymd(first(y, m - 1)), to: ymd(last(y, m - 1)) }
    case "last_3": return { from: ymd(first(y, m - 2)), to: ymd(today) }
    case "last_6": return { from: ymd(first(y, m - 5)), to: ymd(today) }
    case "last_12": return { from: ymd(first(y, m - 11)), to: ymd(today) }
    case "this_year": return { from: ymd(first(y, 0)), to: ymd(today) }
  }
}

/** CSV (separador «;», como o Excel PT) da tabela vendedor × mês. */
export function sellerMonthCsv(report: Report, metric: keyof Counts, metricLabel: string): string {
  const head = ["Vendedor", ...report.months.map(monthLabel), "Total"]
  const fmt = (n: number) => (metric === "wonValue" ? n.toFixed(2).replace(".", ",") : String(n))
  const lines = report.bySeller.map((s) => [s.name, ...report.months.map((m) => fmt(s.months[m]![metric])), fmt(s[metric])])
  return [`${metricLabel}`, head.join(";"), ...lines.map((l) => l.join(";"))].join("\n")
}
